import { useState, useRef, useCallback, useEffect } from 'react';
import type { Attachment, ChatLogEntry, StreamStatus, UsageMetadata, TurnGroup, ThreadItemData } from '../types';
import type {
  PendingHitlInterrupt,
  PendingApprovalInterrupt,
  HitlAnswerDraft,
  ApprovalDecision,
} from '../hitl/types';
import {
  parseApprovalInterruptPayload,
} from '../hitl/types';
import { createLogId } from '../utils/createLogId';
import {
  transformLogsToThreadItems,
  groupByTurn,
  createTurnGroupFromUserMessage,
  TASK_COLORS,
} from '../utils/turnGrouping';
import { classifyKind, uploadAttachments } from '../utils/chatMedia';
import {
  ALLOWED_TRACE_KINDS,
  historicalEntriesFromTraces,
  parseThreadHistoryNdjson,
  type Trace,
} from '../utils/historyTraces';
import aiClient from '@/shared/api/clients/aiClient';
import { useHitlEnabled } from '@/shared/hooks/useHitlEnabled';
import { chatHitlToolRegistry } from '../hitl/registry';

interface StreamingEntry {
  id: string;
  requestId: string;
  agent?: string;
  content: string;
  turn?: number;
}

// User-facing copy for terminal error events that carry a code but no
// message (e.g. the incident chat's thread-ownership rejections).
const CODE_MESSAGES: Record<string, string> = {
  FORBIDDEN: "You don't have access to this conversation.",
  CONFLICT: 'A response is already in progress — try again shortly.',
};

// Fetch and parse the persisted thread history. Returns null when the request
// fails so callers can retry.
// responseType 'text' is required: the body is NDJSON, which axios's default
// JSON transform cannot parse.
async function fetchThreadHistoryTraces(threadId: string): Promise<Trace[] | null> {
  try {
    const { data } = await aiClient.post<string>(
      '/get-thread-history',
      { threadId, limit: 1000 },
      { responseType: 'text' },
    );
    return parseThreadHistoryNdjson(data);
  } catch {
    return null;
  }
}

/**
 * Overrides for pointing the chat stack at a non-report invoke target (e.g.
 * incident chat). Every field defaults to the report-chat behavior, so report
 * callers that omit this are byte-identical to before the option existed.
 * Callers MUST pass a referentially stable object (useMemo) — it participates
 * in hook dependency arrays.
 */
export interface ChatAgentOptions {
  /** Streaming invoke path on the agent API. Default '/invoke'. */
  invokePath?: string;
  /**
   * Replaces the report-centric `configurable` payload entirely. When set,
   * the request body is contract-exact ({threadId, messages, configurable})
   * — no hitlToolRegistry, since non-report targets register no HITL tools.
   */
  configurable?: Record<string, unknown>;
  /** Seeds the thread (history load + invoke) instead of reportId. */
  threadId?: string;
}

interface UseChatAgentProps {
  reportId: string;
  reportType: string;
  /** True while the report is narrative-only (full report not yet generated). */
  narrativeOnly?: boolean;
  evidenceIds?: string[];
  handleSaveChanges?: () => Promise<void>;
  fetchReportData?: () => void;
  showError?: (message: string) => void;
  options?: ChatAgentOptions;
}

export const useChatAgent = ({
  reportId,
  reportType,
  narrativeOnly,
  evidenceIds,
  handleSaveChanges,
  fetchReportData,
  showError,
  options,
}: UseChatAgentProps) => {
  const invokePath = options?.invokePath ?? '/invoke';
  const seedThreadId = options?.threadId ?? reportId;
  const configurableOverride = options?.configurable;
  const hitlEnabled = useHitlEnabled();
  const [logs, setLogs] = useState<ChatLogEntry[]>([]);
  const [turnGroups, setTurnGroups] = useState<TurnGroup[]>([]);
  const [threadId, setThreadId] = useState<string | null>(seedThreadId);
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [usage, setUsage] = useState<UsageMetadata | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  // HITL state — custom tool interrupts (AskUserQuestion pattern)
  const [pendingHitlInterrupts, setPendingHitlInterrupts] = useState<PendingHitlInterrupt[]>([]);
  const [hitlDrafts, setHitlDrafts] = useState<Record<string, HitlAnswerDraft | undefined>>({});

  // HITL state — official middleware approval interrupts (approve/edit/reject)
  const [pendingApprovalInterrupts, setPendingApprovalInterrupts] = useState<PendingApprovalInterrupt[]>([]);
  const [approvalDecisions, setApprovalDecisions] = useState<Record<string, ApprovalDecision | undefined>>({});

  const [isSubmittingHitl, setIsSubmittingHitl] = useState(false);

  // Keep track of streaming responses by requestId (supports parallel agents)
  const streamingEntriesRef = useRef<Map<string, StreamingEntry>>(new Map());

  // Track task color index for consistent coloring across incremental updates
  const taskColorIndexRef = useRef<number>(0);

  // AbortController for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cancellation flag for the timeout-recovery poller. The recovery loop runs
  // its own fetches (not tied to abortControllerRef) and can outlive a user
  // cancel or component unmount, so it checks this flag before every delay,
  // fetch, and state update to stop cleanly. Reset at the start of each send.
  const recoveryCancelledRef = useRef(false);

  // Object URLs created for optimistic image previews. They're carried onto
  // the persisted chat message and shown for the life of the session, so we
  // hold references here and revoke them on unmount to avoid leaking the
  // underlying File blobs.
  const previewUrlsRef = useRef<string[]>([]);

  const revokePreviewUrls = useCallback((urls: Array<string | undefined>) => {
    for (const url of urls) {
      if (!url) continue;
      URL.revokeObjectURL(url);
      previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== url);
    }
  }, []);

  // Revoke any object URLs we created when the hook unmounts, and stop any
  // in-flight timeout recovery so it doesn't update state after unmount.
  useEffect(() => {
    return () => {
      recoveryCancelledRef.current = true;
      for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
      previewUrlsRef.current = [];
    };
  }, []);

  // Load thread history on mount
  useEffect(() => {
    const loadThreadHistory = async () => {
      if (!seedThreadId) {
        setIsLoadingHistory(false);
        return;
      }

      try {
        const traces = await fetchThreadHistoryTraces(seedThreadId);

        if (!traces) {
          setIsLoadingHistory(false);
          return; // No history or error - start fresh
        }

        if (traces.length > 0) {
          const historicalEntries = historicalEntriesFromTraces(traces);
          setLogs(historicalEntries);

          // Compute turn groups once from historical data
          const items = transformLogsToThreadItems(historicalEntries);
          const groups = groupByTurn(items, false);
          setTurnGroups(groups);

          // Update task color index based on how many tasks were in history
          const taskCount = items.filter(item => item.type === 'task').length;
          taskColorIndexRef.current = taskCount;
        }
      } catch (error) {
        console.error('Failed to load thread history:', error);
        // Silently fail - user can still start a new conversation
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadThreadHistory();
  }, [seedThreadId]);

  // Replace the optimistic attachment placeholders on a user entry with the
  // attachment metadata returned from the S3 upload (real s3Keys, etc.) and
  // flip the entry from 'pending' to 'complete'.
  const syncAttachmentsOntoUserEntry = useCallback(
    (entryId: string, attachments: Attachment[]) => {
      setLogs((prev) =>
        prev.map((l) =>
          l.id === entryId ? { ...l, attachments, status: 'complete' } : l,
        ),
      );
      setTurnGroups((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].firstMessage?.id === entryId) {
          updated[lastIdx] = {
            ...updated[lastIdx],
            firstMessage: { ...updated[lastIdx].firstMessage!, attachments },
          };
        }
        return updated;
      });
    },
    [],
  );

  // Recovery path for API Gateway 504s and dropped streaming connections.
  // The agent keeps running server-side and persists its result even after the
  // proxy times out the request, so instead of surfacing an error we poll the
  // thread history until the new turn lands, then rehydrate the UI from the
  // canonical server state. Returns true once recovery succeeds.
  const recoverFromTimeout = useCallback(
    async (newTurnNumber: number): Promise<boolean> => {
      const effectiveThreadId = threadId || seedThreadId;
      if (!effectiveThreadId) return false;
      if (recoveryCancelledRef.current) return false;

      // A completed final response for the new turn signals the agent finished.
      const isTurnComplete = (traces: Trace[]) =>
        traces.some(
          (t) => t.kind === 'response' && typeof t.turn === 'number' && t.turn >= newTurnNumber,
        );
      const responseTraceCount = (traces: Trace[]) =>
        traces.filter((t) => t.kind === 'response').length;

      const hydrate = async (traces: Trace[]) => {
        if (recoveryCancelledRef.current) return;
        const historicalEntries = historicalEntriesFromTraces(traces);
        setLogs(historicalEntries);
        const items = transformLogsToThreadItems(historicalEntries);
        setTurnGroups(groupByTurn(items, false));
        taskColorIndexRef.current = items.filter((item) => item.type === 'task').length;
        if (fetchReportData) await fetchReportData();
      };

      // Baseline the completed-response count at the moment of failure. If the
      // turn already completed (fast agent, buffered 504), recover immediately;
      // otherwise watch for the count to grow (covers traces missing `turn`).
      let baseline = -1;
      try {
        const traces = await fetchThreadHistoryTraces(effectiveThreadId);
        if (traces) {
          if (isTurnComplete(traces)) {
            if (recoveryCancelledRef.current) return false;
            await hydrate(traces);
            return true;
          }
          baseline = responseTraceCount(traces);
        }
      } catch {
        // Ignore — fall through to polling
      }

      const MAX_ATTEMPTS = 24;
      const POLL_INTERVAL_MS = 5000;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (recoveryCancelledRef.current) return false;
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (recoveryCancelledRef.current) return false;
        let traces: Trace[] | null = null;
        try {
          traces = await fetchThreadHistoryTraces(effectiveThreadId);
        } catch {
          continue;
        }
        if (!traces || traces.length === 0) continue;

        if (isTurnComplete(traces) || (baseline >= 0 && responseTraceCount(traces) > baseline)) {
          if (recoveryCancelledRef.current) return false;
          await hydrate(traces);
          return true;
        }
      }
      return false;
    },
    [threadId, seedThreadId, fetchReportData],
  );

  const sendMessage = useCallback(
    async (
      text: string,
      filesOrAudio?: File[] | string,
      hitlResumeData?: { hitlResponses?: any[]; approvalDecisions?: any[] }
    ) => {
      // Second arg accepts File[] (new attachment path) or string (legacy audioBase64 — unused).
      const files: File[] = Array.isArray(filesOrAudio) ? filesOrAudio : [];
      const hasFiles = files.length > 0;

      if ((!text.trim() && !hasFiles && !hitlResumeData) || status === 'streaming') return;

      // Calculate new turn number
      const newTurnNumber = turnGroups.length > 0
        ? turnGroups[turnGroups.length - 1].turn + 1
        : 1;

      let promptText = text;
      if (!text.trim() && hitlResumeData) {
        promptText = 'Provided answers';
        if (hitlResumeData.hitlResponses?.length) {
          promptText = hitlResumeData.hitlResponses.map(r => {
            const input = r.userInput;
            if (typeof input === 'object' && input !== null && 'selectedValues' in input) {
              return (input as any).skipped ? 'Skipped' : ((input as any).selectedValues || []).join(', ');
            }
            return 'Answer provided';
          }).join(' | ');
        } else if (hitlResumeData.approvalDecisions?.length) {
          const actions = hitlResumeData.approvalDecisions.flatMap(d => d.decisions.map((x: any) => x.action));
          promptText = actions.length > 0 ? `Action: ${actions.join(', ')}` : 'Action decided';
        }
      }

      // Build local preview metadata for staged files so the UI shows them
      // immediately while the upload is in flight. The s3Key is filled in
      // after presign returns.
      const pendingAttachments: Attachment[] = files.map((f) => {
        const kind = classifyKind(f.type, f.name) ?? 'image';
        const previewUrl = kind === 'image' ? URL.createObjectURL(f) : undefined;
        if (previewUrl) previewUrlsRef.current.push(previewUrl);
        return {
          s3Key: '',
          contentType: f.type,
          kind,
          fileName: f.name,
          byteSize: f.size,
          previewUrl,
        };
      });

      // Add user request entry
      const userEntry: ChatLogEntry = {
        id: createLogId('user'),
        kind: 'request',
        agent: 'User',
        prompt: promptText,
        ts: new Date().toISOString(),
        status: hasFiles ? 'pending' : 'complete',
        turn: newTurnNumber,
        isHistorical: false,
        attachments: pendingAttachments.length > 0 ? pendingAttachments : undefined,
      };

      setLogs((prev) => [...prev, userEntry]);

      // Create new turn group for user message
      const userItem: ThreadItemData = {
        id: userEntry.id,
        type: 'user',
        content: promptText,
        turn: newTurnNumber,
        isHistorical: false,
        attachments: pendingAttachments.length > 0 ? pendingAttachments : undefined,
      };
      const newTurnGroup = createTurnGroupFromUserMessage(userItem, newTurnNumber);
      setTurnGroups((prev) => [...prev, newTurnGroup]);

      // Fresh send — clear any leftover recovery-cancel flag from a prior turn.
      recoveryCancelledRef.current = false;
      setStatus('streaming');

      // True once the /invoke stream has started, so error handling can tell a
      // dropped stream (recoverable) from a pre-invoke failure (not). True once
      // a terminal `complete`/`error` stream event has been seen.
      let invokeStarted = false;
      let receivedTerminal = false;

      try {
        // 1. Save current report state
        if (handleSaveChanges) {
          await handleSaveChanges();
        }

        // 2. Upload attachments (if any) to S3 via presigned URLs, then sync
        //    the real s3Keys back onto the user entry already in state.
        let uploadedAttachments: Attachment[] = [];
        if (hasFiles) {
          const effectiveThreadId = threadId || seedThreadId;
          if (!effectiveThreadId) {
            throw new Error('Cannot upload attachments without a thread id.');
          }
          try {
            uploadedAttachments = await uploadAttachments(
              effectiveThreadId,
              files,
              pendingAttachments.map((a) => a.previewUrl),
            );
          } catch (uploadError) {
            // Upload failed: the optimistic attachments still carry empty
            // s3Keys. Flip the user entry to 'error' and drop the unusable
            // placeholders so the UI reflects the failure instead of sitting
            // in 'pending' forever. The dropped placeholders are the only
            // referrers of these preview URLs, so revoke them now.
            revokePreviewUrls(pendingAttachments.map((a) => a.previewUrl));
            setLogs((prev) =>
              prev.map((l) =>
                l.id === userEntry.id
                  ? { ...l, status: 'error', attachments: undefined }
                  : l,
              ),
            );
            setTurnGroups((prev) => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (lastIdx >= 0 && updated[lastIdx].firstMessage?.id === userEntry.id) {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  firstMessage: {
                    ...updated[lastIdx].firstMessage!,
                    attachments: undefined,
                  },
                };
              }
              return updated;
            });
            throw uploadError;
          }
          syncAttachmentsOntoUserEntry(userEntry.id, uploadedAttachments);
        }

        // 3. Prepare payload - only send current message (backend tracks history via threadId)
        const payload: Record<string, unknown> = configurableOverride
          ? // Custom invoke targets (incident chat) take a contract-exact body:
            // {threadId, messages, configurable}. No hitlToolRegistry — they
            // register no HITL tools and may reject unknown keys.
            {
              configurable: configurableOverride,
              threadId: threadId,
            }
          : {
              configurable: {
                reportType,
                reportId,
                evidenceIds,
                presentPlanEnabled: hitlEnabled,
                // Narrative-only reports run the narrative workflow on the agent;
                // full reports fall back to the agent's default workflow.
                ...(narrativeOnly ? { workflow: "narrative" } : {}),
              },
              threadId: threadId,
              hitlToolRegistry: chatHitlToolRegistry,
            };

        if (text || uploadedAttachments.length > 0) {
          const message: Record<string, unknown> = {
            role: 'user',
            content: text ? [{ text, type: 'text' }] : [],
          };
          if (uploadedAttachments.length > 0) {
            message.attachments = uploadedAttachments.map((a) => ({
              s3Key: a.s3Key,
              contentType: a.contentType,
              kind: a.kind,
              fileName: a.fileName,
            }));
          }
          payload.messages = [message];
        }
        
        if (hitlResumeData?.hitlResponses) {
          payload.hitlResponses = hitlResumeData.hitlResponses;
          setPendingHitlInterrupts([]);
          setHitlDrafts({});
        }
        
        if (hitlResumeData?.approvalDecisions) {
          payload.approvalDecisions = hitlResumeData.approvalDecisions;
          setPendingApprovalInterrupts([]);
          setApprovalDecisions({});
        }

        // 4. POST to agent API and stream response
        abortControllerRef.current = new AbortController();

        const streamResponse = await aiClient.stream(invokePath, payload, {
          signal: abortControllerRef.current.signal,
        });

        if (!streamResponse.ok) {
          // An API Gateway 504 fires while the agent keeps running and
          // eventually persists its result. Poll the thread history to pick up
          // the completed turn instead of surfacing a hard error.
          if (streamResponse.status === 504) {
            const recovered = await recoverFromTimeout(newTurnNumber);
            if (recovered) {
              setStatus('done');
              return;
            }
            // Recovery was cancelled (user cancel / unmount) — leave the status
            // set by cancelStream in place instead of surfacing an error.
            if (recoveryCancelledRef.current) return;
          }
          throw new Error(`API Error: ${streamResponse.status}`);
        }

        // Throws when the response carried no body. That happens before
        // invokeStarted flips, so it stays a hard failure rather than being
        // treated as a recoverable mid-stream drop.
        const streamLines = streamResponse.lines();
        invokeStarted = true;

        // Reset streaming entries map
        streamingEntriesRef.current.clear();

        for await (const line of streamLines) {
          try {
            const event = JSON.parse(line);

            switch (event.type) {
              case 'start':
                if (event.data?.threadId) {
                  setThreadId(event.data.threadId);
                  // Update user entry with threadId and turn
                  setLogs((prev) =>
                    prev.map((l) =>
                      l.id === userEntry.id
                        ? { ...l, threadId: event.data.threadId, turn: event.data.turn }
                        : l
                    )
                  );
                }
                break;

              case 'token':
                if (event.data?.content) {
                  const content = event.data.content;
                  const tokenAgent = event.data.agent;
                  const tokenRequestId = event.data.requestId;
                  const tokenTurn = event.data.turn;
                  const effectiveRequestId = tokenRequestId || 'default';
                  let textChunk = '';

                  // Handle tool_use tokens (streaming tool calls)
                  if (Array.isArray(content)) {
                    for (const item of content) {
                      if (item.type === 'tool_use' && item.id) {
                        // Create pending tool call entry if not exists
                        setLogs((prev) => {
                          const exists = prev.some(
                            (l) => l.toolCallId === item.id
                          );
                          if (exists) return prev;
                          return [
                            ...prev,
                            {
                              id: createLogId('token-tool'),
                              kind: 'tool_call',
                              tool: item.name || '',
                              args: item.input || '',
                              toolCallId: item.id,
                              ts: new Date().toISOString(),
                              status: 'pending',
                              inputStreaming: true,
                              requestId: tokenRequestId,
                              turn: tokenTurn,
                            } as ChatLogEntry,
                          ];
                        });
                      }

                      // Handle input_json_delta tokens (streaming tool input)
                      if (item.type === 'input_json_delta' && item.input) {
                        // Find the most recent pending tool call and append input
                        setLogs((prev) => {
                          const lastPendingToolIdx = [...prev].reverse().findIndex(
                            (l) => l.kind === 'tool_call' && l.status === 'pending'
                          );
                          if (lastPendingToolIdx === -1) return prev;
                          const actualIdx = prev.length - 1 - lastPendingToolIdx;

                          return prev.map((l, idx) => {
                            if (idx !== actualIdx) return l;
                            const currentArgs = l.args || '';
                            return {
                              ...l,
                              args: currentArgs + item.input,
                              inputStreaming: true,
                            };
                          });
                        });
                      }
                    }

                    // Extract text tokens
                    textChunk = content
                      .filter(
                        (c: { type?: string; text?: string }) =>
                          c.type === 'text' && typeof c.text === 'string'
                      )
                      .map((c: { text: string }) => c.text)
                      .join('');
                  } else if (typeof content === 'string') {
                    textChunk = content;
                  }

                  if (!textChunk) continue;

                  // Get or create streaming entry for this requestId
                  let streamEntry = streamingEntriesRef.current.get(effectiveRequestId);

                  if (!streamEntry) {
                    // Start new streaming response for this requestId
                    const newEntryId = createLogId('streaming');
                    const newEntry: ChatLogEntry = {
                      id: newEntryId,
                      kind: 'response',
                      response: '',
                      agent: tokenAgent,
                      requestId: effectiveRequestId !== 'default' ? effectiveRequestId : undefined,
                      ts: new Date().toISOString(),
                      status: 'streaming',
                      turn: tokenTurn,
                    };
                    setLogs((prev) => [...prev, newEntry]);

                    // Add as lastMessage in turn group
                    const newThreadItem: ThreadItemData = {
                      id: newEntryId,
                      type: 'ai-response',
                      status: 'active',
                      content: '',
                      turn: tokenTurn,
                      isHistorical: false,
                    };
                    setTurnGroups((prev) => {
                      const updated = [...prev];
                      const lastIdx = updated.length - 1;
                      if (lastIdx >= 0) {
                        const lastGroup = updated[lastIdx];
                        // Move current lastMessage to thinkingMessages if exists
                        const newThinking = lastGroup.lastMessage
                          ? [...lastGroup.thinkingMessages, lastGroup.lastMessage]
                          : lastGroup.thinkingMessages;
                        updated[lastIdx] = {
                          ...lastGroup,
                          thinkingMessages: newThinking,
                          lastMessage: newThreadItem,
                        };
                      }
                      return updated;
                    });

                    streamEntry = {
                      id: newEntry.id,
                      requestId: effectiveRequestId,
                      agent: tokenAgent,
                      content: '',
                      turn: tokenTurn,
                    };
                    streamingEntriesRef.current.set(effectiveRequestId, streamEntry);
                  }

                  // Accumulate content
                  streamEntry.content += textChunk;

                  const activeId = streamEntry.id;
                  const activeContent = streamEntry.content;

                  setLogs((prev) =>
                    prev.map((l) =>
                      l.id === activeId ? { ...l, response: activeContent } : l
                    )
                  );

                  // Update turn group's lastMessage content
                  setTurnGroups((prev) => {
                    const updated = [...prev];
                    const lastIdx = updated.length - 1;
                    if (lastIdx >= 0 && updated[lastIdx].lastMessage?.id === activeId) {
                      updated[lastIdx] = {
                        ...updated[lastIdx],
                        lastMessage: {
                          ...updated[lastIdx].lastMessage!,
                          content: activeContent,
                        },
                      };
                    }
                    return updated;
                  });
                }
                break;

              case 'trace': {
                const data = event.data || {};
                const { kind, tool, args, response: traceResponse, toolCallId, agent, durationMs, usage, requestId: traceRequestId, turn: traceTurn } = data;

                // Handle HITL interrupt events (custom tools + official middleware)
                if (kind === 'hitl_interrupt') {
                  const payload = data.payload || {};
                  setPendingHitlInterrupts((prev) => [
                    ...prev,
                    {
                      interruptId: data.id || `hitl-${Date.now()}`,
                      threadId: data.threadId,
                      namespace: data.namespace,
                      toolName: payload.toolName || 'unknown',
                      toolCallId: payload.toolCallId,
                      toolArgs: payload.toolArgs,
                      schema: payload.schema,
                      ts: data.ts || new Date().toISOString(),
                      turn: traceTurn,
                      source: 'stream' as const,
                    },
                  ]);
                  break;
                }

                if (kind === 'hitl_approval_interrupt') {
                  const payload = data.payload || {};
                  const approvalPayload = parseApprovalInterruptPayload(payload);
                  if (approvalPayload) {
                    setPendingApprovalInterrupts((prev) => [
                      ...prev,
                      {
                        interruptId: data.id || `approval-${Date.now()}`,
                        threadId: data.threadId,
                        namespace: data.namespace,
                        payload: approvalPayload,
                        ts: data.ts || new Date().toISOString(),
                        turn: traceTurn,
                        source: 'stream' as const,
                      },
                    ]);
                  }
                  break;
                }

                // Skip trace kinds not in the allowed list (same filtering as history)
                if (!ALLOWED_TRACE_KINDS.includes(kind)) {
                  break;
                }

                const effectiveTraceRequestId = traceRequestId || 'default';

                if (kind === 'tool_call') {
                  const toolEntryId = toolCallId || createLogId('trace-tool');
                  // Create pending tool call entry
                  setLogs((prev) => {
                    const exists = prev.some((l) => l.toolCallId === toolCallId);
                    if (exists) return prev;

                    const argsStr = typeof args === 'string' ? args : JSON.stringify(args);
                    return [
                      ...prev,
                      {
                        id: toolEntryId,
                        kind: 'tool_call',
                        tool,
                        args: argsStr,
                        toolCallId,
                        agent,
                        requestId: traceRequestId,
                        ts: new Date().toISOString(),
                        status: 'pending',
                        turn: traceTurn,
                      } as ChatLogEntry,
                    ];
                  });

                  // Add tool call to turn group's thinkingMessages
                  const isTask = tool?.toLowerCase() === 'task';
                  let title = 'Working...';
                  if (isTask) {
                    try {
                      const parsedArgs = typeof args === 'string' ? JSON.parse(args) : args || {};
                      title = (parsedArgs.description as string) || `${parsedArgs.subagent_type || 'Agent'}: Working...`;
                    } catch {
                      title = 'Task';
                    }
                  }

                  const toolItem: ThreadItemData = {
                    id: toolEntryId,
                    type: isTask ? 'task' : 'working',
                    status: 'active',
                    title,
                    taskColor: isTask ? TASK_COLORS[taskColorIndexRef.current % TASK_COLORS.length] : undefined,
                    turn: traceTurn,
                    isHistorical: false,
                  };

                  if (isTask) {
                    taskColorIndexRef.current++;
                  }

                  setTurnGroups((prev) => {
                    const updated = [...prev];
                    const lastIdx = updated.length - 1;
                    if (lastIdx >= 0) {
                      // Check if this tool call already exists in thinkingMessages
                      const exists = updated[lastIdx].thinkingMessages.some(m => m.id === toolEntryId);
                      if (!exists) {
                        updated[lastIdx] = {
                          ...updated[lastIdx],
                          thinkingMessages: [...updated[lastIdx].thinkingMessages, toolItem],
                        };
                      }
                    }
                    return updated;
                  });
                } else if (kind === 'tool_response') {
                  // Merge response into existing tool call
                  setLogs((prev) => {
                    const existingIdx = prev.findIndex(
                      (l) => l.kind === 'tool_call' && l.toolCallId === toolCallId
                    );
                    if (existingIdx === -1) return prev;

                    const responseStr = typeof traceResponse === 'string'
                      ? traceResponse
                      : JSON.stringify(traceResponse);

                    return prev.map((l, idx) =>
                      idx === existingIdx
                        ? { ...l, response: responseStr, status: 'complete', durationMs }
                        : l
                    );
                  });

                  // Update tool status in turn group's thinkingMessages
                  setTurnGroups((prev) => {
                    const updated = [...prev];
                    const lastIdx = updated.length - 1;
                    if (lastIdx >= 0) {
                      const thinkingMessages = updated[lastIdx].thinkingMessages.map(m =>
                        m.id === toolCallId ? { ...m, status: 'completed' as const } : m
                      );
                      updated[lastIdx] = { ...updated[lastIdx], thinkingMessages };
                    }
                    return updated;
                  });
                } else if (kind === 'response') {
                  // Handle response trace - display text and accumulate usage
                  if (traceResponse) {
                    // Finalize streaming entry by requestId
                    const streamEntry = streamingEntriesRef.current.get(effectiveTraceRequestId);
                    if (streamEntry) {
                      const streamingId = streamEntry.id;
                      setLogs((prev) =>
                        prev.map((l) =>
                          l.id === streamingId
                            ? { ...l, agent, status: 'complete', requestId: traceRequestId, turn: traceTurn }
                            : l
                        )
                      );

                      // Update turn group's lastMessage status to completed
                      setTurnGroups((prev) => {
                        const updated = [...prev];
                        const lastIdx = updated.length - 1;
                        if (lastIdx >= 0 && updated[lastIdx].lastMessage?.id === streamingId) {
                          updated[lastIdx] = {
                            ...updated[lastIdx],
                            lastMessage: {
                              ...updated[lastIdx].lastMessage!,
                              status: 'completed',
                            },
                          };
                        }
                        return updated;
                      });

                      streamingEntriesRef.current.delete(effectiveTraceRequestId);
                    } else {
                      // Create new response entry from trace
                      const newEntryId = data.id || createLogId('trace-response');
                      const newEntry: ChatLogEntry = {
                        id: newEntryId,
                        kind: 'response',
                        agent,
                        response: traceResponse,
                        requestId: traceRequestId,
                        ts: data.ts || new Date().toISOString(),
                        status: 'complete',
                        durationMs,
                        turn: traceTurn,
                      };
                      setLogs((prev) => [...prev, newEntry]);

                      // Add as lastMessage in turn group
                      const newThreadItem: ThreadItemData = {
                        id: newEntryId,
                        type: 'ai-response',
                        status: 'completed',
                        content: traceResponse,
                        turn: traceTurn,
                        isHistorical: false,
                      };
                      setTurnGroups((prev) => {
                        const updated = [...prev];
                        const lastIdx = updated.length - 1;
                        if (lastIdx >= 0) {
                          const lastGroup = updated[lastIdx];
                          // Move current lastMessage to thinkingMessages if exists
                          const newThinking = lastGroup.lastMessage
                            ? [...lastGroup.thinkingMessages, lastGroup.lastMessage]
                            : lastGroup.thinkingMessages;
                          updated[lastIdx] = {
                            ...lastGroup,
                            thinkingMessages: newThinking,
                            lastMessage: newThreadItem,
                          };
                        }
                        return updated;
                      });
                    }
                  }

                  // Accumulate usage metadata
                  if (usage) {
                    setUsage((prev) => ({
                      input_tokens: (prev?.input_tokens || 0) + (usage.input_tokens || 0),
                      output_tokens: (prev?.output_tokens || 0) + (usage.output_tokens || 0),
                      total_tokens: (prev?.total_tokens || 0) + (usage.total_tokens || 0),
                    }));
                  }
                }
                break;
              }

              case 'complete': {
                receivedTerminal = true;
                abortControllerRef.current = null;
                // Finalize ALL remaining streaming entries
                const remainingEntries = Array.from(streamingEntriesRef.current.values());
                if (remainingEntries.length > 0) {
                  const idsToComplete = remainingEntries.map(e => e.id);
                  setLogs((prev) =>
                    prev.map((l) =>
                      idsToComplete.includes(l.id) ? { ...l, status: 'complete' } : l
                    )
                  );
                  streamingEntriesRef.current.clear();
                }

                // Mark turn group as not streaming
                setTurnGroups((prev) => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (lastIdx >= 0) {
                    updated[lastIdx] = { ...updated[lastIdx], isStreaming: false };
                    // Also mark lastMessage as completed if still active
                    if (updated[lastIdx].lastMessage?.status === 'active') {
                      updated[lastIdx] = {
                        ...updated[lastIdx],
                        lastMessage: {
                          ...updated[lastIdx].lastMessage!,
                          status: 'completed',
                        },
                      };
                    }
                  }
                  return updated;
                });

                setStatus('done');
                // Reload report data
                if (fetchReportData) {
                  await fetchReportData();
                }
                break;
              }

              case 'error': {
                receivedTerminal = true;
                // Mark all streaming entries as error
                const errorEntries = Array.from(streamingEntriesRef.current.values());
                if (errorEntries.length > 0) {
                  const idsToError = errorEntries.map(e => e.id);
                  setLogs((prev) =>
                    prev.map((l) =>
                      idsToError.includes(l.id)
                        ? { ...l, status: 'error', response: l.response || 'Error occurred' }
                        : l
                    )
                  );
                  streamingEntriesRef.current.clear();
                }
                if (showError)
                  showError(
                    event.data?.message ||
                      CODE_MESSAGES[event.data?.code] ||
                      event.data?.code ||
                      'Unknown error'
                  );
                setStatus('error');
                break;
              }
            }
          } catch (e) {
            console.error('Error parsing stream line:', e);
          }
        }

        // Stream ended. If no terminal `complete`/`error` event arrived, the
        // connection was dropped mid-stream even though the agent keeps running
        // server-side. Recover by polling the thread history.
        if (!receivedTerminal) {
          const recovered = await recoverFromTimeout(newTurnNumber);
          if (recovered) {
            setStatus('done');
            return;
          }
          // Recovery was cancelled (user cancel / unmount) — leave the status
          // set by cancelStream in place instead of surfacing an error.
          if (recoveryCancelledRef.current) return;
          // Recovery failed — the turn never landed server-side. Surface an
          // error instead of falling through to 'done', which would falsely
          // report success on a dropped stream.
          if (showError) showError('Connection lost and could not recover the response.');
          setStatus('error');
          return;
        }

        // Stream ended - ensure status is set to done
        setStatus((currentStatus) =>
          currentStatus === 'streaming' ? 'done' : currentStatus
        );
      } catch (error: unknown) {
        // Check if this was an intentional abort
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Stream cancelled by user');
          setStatus('idle');
          return;
        }

        // A 504 or dropped connection can abort the stream while the agent
        // keeps running and persists its result. Try to recover by polling the
        // thread history before surfacing an error to the user.
        if (invokeStarted) {
          const recovered = await recoverFromTimeout(newTurnNumber);
          if (recovered) {
            setStatus('done');
            return;
          }
          // Recovery was cancelled (user cancel / unmount) — leave the status
          // set by cancelStream in place instead of surfacing an error.
          if (recoveryCancelledRef.current) return;
        }

        console.error('Chat error:', error);
        if (showError)
          showError(error instanceof Error ? error.message : 'Unknown error');

        // Update all streaming entries to error state
        const catchErrorEntries = Array.from(streamingEntriesRef.current.values());
        if (catchErrorEntries.length > 0) {
          const idsToError = catchErrorEntries.map(e => e.id);
          setLogs((prev) =>
            prev.map((l) =>
              idsToError.includes(l.id)
                ? {
                    ...l,
                    status: 'error',
                    response: l.response || 'Error occurred.',
                  }
                : l
            )
          );
          streamingEntriesRef.current.clear();
        } else {
          // Fallback error entry if no streaming entries exist
          const errorEntry: ChatLogEntry = {
            id: createLogId('error'),
            kind: 'error',
            response: 'Failed to send message. Please try again.',
            ts: new Date().toISOString(),
            status: 'error',
          };
          setLogs((prev) => [...prev, errorEntry]);
        }
        setStatus('error');
      } finally {
        setStatus((currentStatus) =>
          currentStatus === 'streaming' ? 'done' : currentStatus
        );
        streamingEntriesRef.current.clear();
      }
    },
    [
      invokePath,
      seedThreadId,
      configurableOverride,
      turnGroups,
      threadId,
      status,
      handleSaveChanges,
      fetchReportData,
      showError,
      reportType,
      narrativeOnly,
      reportId,
      evidenceIds,
      hitlEnabled,
      syncAttachmentsOntoUserEntry,
      revokePreviewUrls,
      recoverFromTimeout,
    ]
  );

  const cancelStream = useCallback(() => {
    // Stop any in-flight timeout recovery so its poller doesn't resurrect
    // 'done'/'error' state after the user has cancelled.
    recoveryCancelledRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;

      // Mark ALL streaming entries as cancelled
      const cancelEntries = Array.from(streamingEntriesRef.current.values());
      if (cancelEntries.length > 0) {
        const idsToCancel = cancelEntries.map(e => e.id);
        setLogs((prev) =>
          prev.map((l) =>
            idsToCancel.includes(l.id)
              ? { ...l, status: 'error', response: l.response || 'Cancelled' }
              : l
          )
        );
        streamingEntriesRef.current.clear();
      }

      setStatus('idle');
    }
  }, []);

  // --- HITL resume submission ---

  const submitHitlResume = useCallback(async (
    hitlResponses: Array<{ interruptId: string; userInput: unknown }>,
    approvalDecisionPayloads?: Array<{ interruptId: string; decisions: ApprovalDecision[] }>,
  ) => {
    setIsSubmittingHitl(true);
    try {
      await sendMessage('', undefined, { hitlResponses, approvalDecisions: approvalDecisionPayloads });
    } finally {
      setIsSubmittingHitl(false);
    }
  }, [sendMessage]);

  const handleHitlDraftChange = useCallback((interruptId: string, draft: HitlAnswerDraft) => {
    setHitlDrafts((prev) => ({ ...prev, [interruptId]: draft }));
  }, []);

  const handleApprovalDecisionChange = useCallback((interruptId: string, decision: ApprovalDecision) => {
    setApprovalDecisions((prev) => ({ ...prev, [interruptId]: decision }));
  }, []);

  const hasActiveInterrupts = pendingHitlInterrupts.length > 0 || pendingApprovalInterrupts.length > 0;

  return {
    logs,
    turnGroups,
    sendMessage,
    cancelStream,
    isStreaming: status === 'streaming',
    status,
    usage,
    threadId,
    isLoadingHistory,
    // HITL — custom tool interrupts
    pendingHitlInterrupts,
    hitlDrafts,
    handleHitlDraftChange,
    // HITL — approval interrupts
    pendingApprovalInterrupts,
    approvalDecisions,
    handleApprovalDecisionChange,
    // HITL — shared
    submitHitlResume,
    isSubmittingHitl,
    hasActiveInterrupts,
  };
};

// Re-export types for convenience
export type { ChatLogEntry, StreamStatus, UsageMetadata };
